import { create } from "zustand";
import {
  Mindspace,
  ContextItem,
  fetchMindspaces,
  fetchContextItems,
  createContextItem,
  createMindspace,
  updateMindspace,
  deleteMindspace,
  setDefaultMindspace,
  CreateContextItemRequest,
} from "../services/apiClientV2";
import { logDebug, logError } from "../utils/logging";
import { trackEvent } from "../services/analyticsService";
import useAuthStore from "./authStore";

interface MindspaceState {
  // State
  mindspaces: Mindspace[];
  activeMindspaceId: string | null;
  contextItems: ContextItem[];
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;
  retryCount: number;
  canRetry: boolean;

  // Actions
  loadMindspaces: () => Promise<void>;
  setActiveMindspace: (mindspaceId: string) => Promise<void>;
  createMindspace: (name: string, description?: string) => Promise<Mindspace>;
  updateMindspace: (id: string, updates: { name?: string, description?: string }) => Promise<void>;
  deleteMindspace: (id: string) => Promise<void>;
  setDefaultMindspace: (id: string) => Promise<void>;

  loadContextItems: (mindspaceId: string) => Promise<void>;
  updateContextItem: (key: string, content: string) => Promise<void>;
  clearError: () => void;

  // Computed / Helpers
  getActiveMindspace: () => Mindspace | undefined;
  getContextValue: (key: string) => string;
  hasContextValue: (key: string) => boolean;
  updateMultipleContextItems: (
    contextUpdates: Record<string, string>,
  ) => Promise<void>;
}

export const useMindspaceStore = create<MindspaceState>((set, get) => ({
  // Initial state
  mindspaces: [],
  activeMindspaceId: null,
  contextItems: [],
  isLoading: false,
  error: null,
  isInitialized: false,
  retryCount: 0,
  canRetry: true,

  // Load user's mindspaces
  loadMindspaces: async () => {
    const { isGuest } = useAuthStore.getState();

    // Guests do not have mindspaces; mark initialized and skip loading
    if (isGuest) {
      logDebug("[mindspaceStore] Guest mode detected – skipping mindspace load");
      set({ isInitialized: true, mindspaces: [], activeMindspaceId: null, contextItems: [] });
      return;
    }

    set({ isLoading: true, error: null });

    try {
      logDebug("[mindspaceStore] Loading mindspaces...");
      const mindspaces = await fetchMindspaces();

      // Determine active mindspace: default > first > null
      // Determine active mindspace: saved > default > first > null
      const savedMindspaceId = localStorage.getItem("smartie_last_active_mindspace");
      const defaultMindspace = mindspaces.find(m => m.is_default);

      let initialActiveId = defaultMindspace?.id || mindspaces[0]?.id || null;

      if (savedMindspaceId) {
        const savedExists = mindspaces.find(m => m.id === savedMindspaceId);
        if (savedExists) {
          initialActiveId = savedMindspaceId;
        }
      }

      const activeMindspaceId = initialActiveId;

      logDebug(`[mindspaceStore] Loaded ${mindspaces.length} mindspaces. Active: ${activeMindspaceId}`);

      set({
        mindspaces,
        activeMindspaceId,
        isLoading: false,
        isInitialized: true,
        error: null
      });

      // Load context for the active mindspace if one exists
      if (activeMindspaceId) {
        await get().loadContextItems(activeMindspaceId);
      }
    } catch (error) {
      logError("[mindspaceStore] Error loading mindspaces:", error);
      set({
        isLoading: false,
        error: "Failed to load mindspaces. Please refresh.",
        isInitialized: true // Mark initialized to prevent infinite loading spinners
      });
    }
  },

  setActiveMindspace: async (mindspaceId: string) => {
    const state = get();
    if (state.activeMindspaceId === mindspaceId) return;

    const mindspace = state.mindspaces.find(m => m.id === mindspaceId);
    if (!mindspace) {
      logError(`[mindspaceStore] Cannot set active mindspace: ID ${mindspaceId} not found`);
      return;
    }

    logDebug(`[mindspaceStore] Switching active mindspace to: ${mindspace.name} (${mindspaceId})`);
    set({ activeMindspaceId: mindspaceId, contextItems: [] }); // Clear context while loading new

    // Load context for the new mindspace
    await get().loadContextItems(mindspaceId);

    // Stop any active streaming and refresh chat history scoped to the new mindspace
    try {
      const { useChatStore } = await import("./chatStore");
      const chatStore = useChatStore.getState();

      // If currently streaming, abort it cleanly before switching
      if (chatStore.isStreaming) {
        logDebug("[mindspaceStore] Aborting active stream before mindspace switch");
        chatStore.stopStreaming();
      }

      chatStore.startNewChatSession();
      chatStore.fetchSessionSummaries();
    } catch (e) {
      logError("[mindspaceStore] Could not refresh chat history after switch:", e);
    }

    trackEvent("mindspace_switched", { mindspace_id: mindspaceId, name: mindspace.name });
    localStorage.setItem("smartie_last_active_mindspace", mindspaceId);
  },

  createMindspace: async (name: string, description?: string) => {
    set({ isLoading: true, error: null });
    try {
      const newMindspace = await createMindspace(name, description);
      set(state => ({
        mindspaces: [...state.mindspaces, newMindspace],
        // If it's the first mindspace, make it active
        activeMindspaceId: state.mindspaces.length === 0 ? newMindspace.id : state.activeMindspaceId,
        isLoading: false
      }));

      trackEvent("mindspace_created", { name });
      return newMindspace;
    } catch (error: unknown) {
      logError("[mindspaceStore] Error creating mindspace:", error);
      // Extract error message from API response if possible
      const msg = error instanceof Error ? error.message : "Failed to create mindspace";
      set({ error: msg, isLoading: false });
      throw error;
    }
  },

  updateMindspace: async (id: string, updates: { name?: string, description?: string }) => {
    set({ isLoading: true, error: null });
    try {
      const updated = await updateMindspace(id, updates);
      set(state => ({
        mindspaces: state.mindspaces.map(m => m.id === id ? updated : m),
        isLoading: false
      }));
    } catch (error: unknown) {
      logError("[mindspaceStore] Error updating mindspace:", error);
      const msg = error instanceof Error ? error.message : "Failed to update mindspace";
      set({ error: msg, isLoading: false });
      throw error;
    }
  },

  deleteMindspace: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await deleteMindspace(id);

      const state = get();
      const updatedMindspaces = state.mindspaces.filter(m => m.id !== id);

      // If we deleted the active mindspace, switch to default or first available
      let newActiveId = state.activeMindspaceId;
      if (state.activeMindspaceId === id) {
        const defaultM = updatedMindspaces.find(m => m.is_default);
        newActiveId = defaultM?.id || updatedMindspaces[0]?.id || null;
      }

      set({
        mindspaces: updatedMindspaces,
        activeMindspaceId: newActiveId,
        isLoading: false
      });

      // specific logic: if we switched mindspace, load its context
      if (newActiveId && newActiveId !== state.activeMindspaceId) {
        get().loadContextItems(newActiveId);
      }

    } catch (error: unknown) {
      logError("[mindspaceStore] Error deleting mindspace:", error);
      const msg = error instanceof Error ? error.message : "Failed to delete mindspace";
      set({ error: msg, isLoading: false });
      throw error;
    }
  },

  setDefaultMindspace: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await setDefaultMindspace(id);

      // Update local state: set new default, unset old default
      set(state => ({
        mindspaces: state.mindspaces.map(m => ({
          ...m,
          is_default: m.id === id // Only the target is default
        })),
        isLoading: false
      }));
    } catch (error: unknown) {
      logError("[mindspaceStore] Error setting default mindspace:", error);
      const msg = error instanceof Error ? error.message : "Failed to set default mindspace";
      set({ error: msg, isLoading: false });
      throw error;
    }
  },

  // Load context items for a mindspace
  loadContextItems: async (mindspaceId: string) => {
    try {
      logDebug("[mindspaceStore] Loading context items for mindspace:", mindspaceId);
      const contextItems = await fetchContextItems(mindspaceId);
      set({ contextItems });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to load context items";
      logError("[mindspaceStore] Error loading context items:", error);
      set({ error: errorMessage });
    }
  },

  // Update or create a context item (upsert for canonical keys)
  updateContextItem: async (key: string, content: string) => {
    const state = get();
    if (!state.activeMindspaceId) {
      throw new Error("No active mindspace selected");
    }

    try {
      logDebug("[mindspaceStore] Updating context item:", key, content);

      const contextItemRequest: CreateContextItemRequest = {
        item_type: key,
        content: content.trim(),
        metadata: {},
      };

      const updatedItem = await createContextItem(
        state.activeMindspaceId,
        contextItemRequest,
      );

      // Update local state optimistically
      const existingItemIndex = state.contextItems.findIndex(
        (item) => item.item_type === key,
      );
      let newContextItems: ContextItem[];

      if (existingItemIndex >= 0) {
        // Update existing item
        newContextItems = [...state.contextItems];
        newContextItems[existingItemIndex] = updatedItem;
      } else {
        // Add new item
        newContextItems = [...state.contextItems, updatedItem];
      }

      set({ contextItems: newContextItems });

      // Track context update
      trackEvent("context_item_updated", {
        mindspace_id: state.activeMindspaceId,
        item_type: key,
        content_length: content.length,
      });

    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to update context item";
      logError("[mindspaceStore] Error updating context item:", error);
      set({ error: errorMessage });
      throw error;
    }
  },

  // Clear error state
  clearError: () => {
    set({ error: null });
  },

  // Helper methods
  getActiveMindspace: () => {
    const state = get();
    return state.mindspaces.find(m => m.id === state.activeMindspaceId);
  },

  getContextValue: (key: string) => {
    const state = get();
    const item = state.contextItems.find((item) => item.item_type === key);
    return item?.content || "";
  },

  hasContextValue: (key: string) => {
    const state = get();
    return state.contextItems.some(
      (item) => item.item_type === key && item.content.trim() !== "",
    );
  },

  // Batch update multiple context items
  updateMultipleContextItems: async (
    contextUpdates: Record<string, string>,
  ) => {
    const state = get();
    if (!state.activeMindspaceId) {
      throw new Error("No active mindspace selected");
    }

    const promises = Object.entries(contextUpdates).map(([key, content]) => {
      if (content.trim() !== "") {
        return get().updateContextItem(key, content);
      }
      return Promise.resolve();
    });

    try {
      await Promise.all(promises);
      logDebug("[mindspaceStore] Batch context update completed");
    } catch (error) {
      logError("[mindspaceStore] Error in batch context update:", error);
      throw error;
    }
  },
}));

export default useMindspaceStore;
